import { useEffect, useState } from "react";
import type { Peer } from "@circle-react/types/Live/Peer";

const DEFAULT_PAGE_SIZE = 6;

interface PaginatedPeersOptions {
  offset?: number;
}

type PeerHolder = Peer | null;

export const usePaginatedPeers = (
  peers: Peer[],
  startingPage = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  { offset = 0 }: PaginatedPeersOptions = {},
) => {
  const offsetValue = Math.max(0, offset);
  // Simulate prepending empty items for offset: null means an empty slot
  const offsetPeers: PeerHolder[] =
    offsetValue > 0 ? Array<PeerHolder>(offsetValue).fill(null) : [];
  const effectivePeers: PeerHolder[] = [...offsetPeers, ...peers];
  const [page, setPage] = useState(startingPage);
  const startingPeerIndex = (page - 1) * pageSize;
  const currentPagePeers = effectivePeers.slice(
    startingPeerIndex,
    startingPeerIndex + pageSize,
  );
  const totalPages = Math.ceil(effectivePeers.length / pageSize);
  const next = () => setPage(Math.min(page + 1, totalPages));
  const prev = () => setPage(Math.max(page - 1, 1));
  const goToPage = (pageNumber: number) =>
    setPage(Math.max(1, Math.min(pageNumber, totalPages)));
  const isFirstPage = page === 1;
  const isLastPage = page === totalPages;

  // when totalPages changes, we need to navigate to previous page if it's out of bounds
  useEffect(() => {
    if (page > totalPages) {
      setPage(Math.max(totalPages, 1));
    }
  }, [page, totalPages]);

  return {
    currentPagePeers: currentPagePeers.filter(
      (peer): peer is Peer => peer !== null,
    ),
    page,
    totalPages,
    next,
    prev,
    goToPage,
    isFirstPage,
    isLastPage,
  };
};
